import html2canvas from 'html2canvas';
import { ResumenPremiosPayload, renderResumenPremiosHTML } from '../services/ticketService';
import { shareImageNative } from './nativeShare';
import { isNativePlatform } from './platform';

/**
 * Genera una imagen JPG del resumen de premios (ganadores) a partir del HTML existente.
 */
export async function generateResumenImage(payload: ResumenPremiosPayload): Promise<Blob> {
  const html = renderResumenPremiosHTML(payload);
  const native = isNativePlatform();

  const anchoPx = native ? 440 : 400;
  const scale = native ? 2.3 : 2.2;
  const jpegQuality = native ? 0.95 : 0.92;

  const styleMatch = html.match(/<style>([\s\S]*?)<\/style>/);
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/);

  if (!bodyMatch) {
    throw new Error('No se pudo extraer el contenido del resumen');
  }

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.zIndex = '-1';

  const wrapper = document.createElement('div');
  wrapper.style.width = `${anchoPx}px`;
  wrapper.style.maxWidth = `${anchoPx}px`;
  wrapper.style.fontFamily = "'Courier New', Courier, monospace";
  wrapper.style.fontSize = '13px';
  wrapper.style.fontWeight = '700';
  wrapper.style.lineHeight = '1.35';
  wrapper.style.color = '#000';
  wrapper.style.background = '#fff';
  wrapper.style.padding = '10px 12px 16px';
  wrapper.style.textAlign = 'left';
  wrapper.style.boxSizing = 'border-box';
  wrapper.style.overflow = 'visible';
  wrapper.style.overflowWrap = 'break-word';
  wrapper.style.wordBreak = 'break-word';

  if (styleMatch) {
    const styleEl = document.createElement('style');
    styleEl.textContent = styleMatch[1];
    container.appendChild(styleEl);
  }

  // Estilos override para captura de imagen (el CSS original es para impresora 48mm)
  const captureStyle = document.createElement('style');
  captureStyle.textContent = `
    .meta-row {
      grid-template-columns: 80px 1fr !important;
      column-gap: 6px !important;
      margin: 2px 0 !important;
    }
    .meta-label, .meta-value {
      font-size: 12px !important;
      overflow: visible !important;
      white-space: normal !important;
      text-overflow: clip !important;
    }
    .tk-header, .tk-row {
      grid-template-columns: 1fr 70px 80px !important;
      column-gap: 6px !important;
    }
    .tk-header span, .tk-row span {
      font-size: 11px !important;
      overflow: visible !important;
      white-space: nowrap !important;
    }
    .tk-id {
      overflow: visible !important;
      white-space: nowrap !important;
      text-overflow: clip !important;
      font-size: 12px !important;
    }
    .tk-val {
      font-size: 12px !important;
      white-space: nowrap !important;
    }
    .total-row {
      grid-template-columns: 1fr auto !important;
      column-gap: 8px !important;
      font-size: 14px !important;
    }
    .title { font-size: 16px !important; }
    .subtitle { font-size: 13px !important; }
    .sep { margin: 4px 0 !important; }
    .footer { font-size: 12px !important; }
    .fecha-imp { font-size: 11px !important; }
  `;
  container.appendChild(captureStyle);

  wrapper.innerHTML = bodyMatch[1].replace(/<script[\s\S]*?<\/script>/gi, '');

  const bottomSpacer = document.createElement('div');
  bottomSpacer.style.height = '8px';
  bottomSpacer.style.background = '#fff';
  bottomSpacer.style.width = '100%';
  wrapper.appendChild(bottomSpacer);

  container.appendChild(wrapper);
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(wrapper, {
      scale,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: anchoPx,
      windowWidth: anchoPx,
    });

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Error generando imagen'))),
        'image/jpeg',
        jpegQuality,
      );
    });

    return blob;
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Genera imagen del resumen de premios y la comparte/descarga.
 * - APK: abre share sheet nativo
 * - Web/PWA: navigator.share o descarga directa
 */
export async function shareResumenAsImage(payload: ResumenPremiosPayload): Promise<boolean> {
  const blob = await generateResumenImage(payload);
  const fileName = `ganadores-${payload.fecha}-${payload.sorteo.replace(/\s+/g, '_')}.jpg`;
  const title = `Ganadores ${payload.sorteo} - ${payload.fecha}`;

  return shareImageNative(blob, fileName, title);
}

export async function downloadResumenAsImage(payload: ResumenPremiosPayload): Promise<boolean> {
  const blob = await generateResumenImage(payload);
  const fileName = `ganadores-${payload.fecha}-${payload.sorteo.replace(/\s+/g, '_')}.jpg`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}
